import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import { useSupabase } from "../../auth/useSupabase";
import { gameService } from "../../data/gameService";
import leagueService from "../../data/leagueService";
import { teamService } from "../../data/teamService";
import { Tables, TablesInsert } from "../../../database.types";
import { getErrorMessage } from "../../utils/utils";
import { cloneDeep } from "lodash";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { useNotification } from "../../contexts/NotificationContext";
import { App } from "../../types/types";

const generateRandomHexCode = (): string => {
  return (
    "#" +
    Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, "0")
  );
};

interface UseLeagueOverviewParams {
  league: Tables<"leagues">;
  onLeagueUpdate?: (league: Tables<"leagues">) => void;
}

export const useLeagueOverview = ({
  league,
  onLeagueUpdate,
}: UseLeagueOverviewParams) => {
  const { user, supabase, signOut } = useSupabase();
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { showSnackbar } = useNotification();

  // State for data
  const [myGames, setMyGames] = useState<App.GameWithCreator[]>([]);
  const [leagueGames, setLeagueGames] = useState<App.GameWithCreator[]>([]);
  const [teams, setTeams] = useState<App.TeamWithMembers[]>([]);
  const [leagueMembers, setLeagueMembers] = useState<
    App.LeagueMemberWithProfile[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const isOwner = league.creator_id === user?.id;

  const currentUserMembership = useMemo(
    () => leagueMembers.find((m) => m.user_id === user?.id) ?? null,
    [leagueMembers, user?.id],
  );

  const unassignedMembers = useMemo(
    () => leagueMembers.filter((m) => !m.team_id),
    [leagueMembers],
  );

  const [gameToStart, setGameToStart] = useState<App.GameWithCreator | null>(
    null,
  );

  const [confirmSignOut, setConfirmSignOut] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useLocalStorageState<
    "teams" | "my-games" | "league-games"
  >("active-tab", "teams");

  // Lookup maps for teams
  const [teamsMap, setTeamsMap] = useState<Record<string, Tables<"teams">>>({});

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(league.name);

  const [newGameName, setNewGameName] = useState<string>("");
  const [newTeam, setNewTeam] = useState<TablesInsert<"teams">>({
    name: "",
    color: "",
    league_id: league?.id ?? "",
  });

  const onSaveLeagueName = async () => {
    const trimmed = editedName.trim();
    if (!trimmed || trimmed === league.name) {
      setEditedName(league.name);
      setIsEditingName(false);
      return;
    }
    try {
      const updated = await leagueService.update(supabase, league.id, {
        name: trimmed,
      });
      onLeagueUpdate?.(updated);
      showSnackbar("League name updated", "success");
    } catch (err) {
      showSnackbar(getErrorMessage(err), "error");
      setEditedName(league.name);
    } finally {
      setIsEditingName(false);
    }
  };

  const fetchMyGames = async () => {
    if (!league || !user) return;

    try {
      const data = await gameService.listMyLeagueGames(
        supabase,
        league.id,
        user.id,
      );
      setMyGames(data);
    } catch (err) {
      showSnackbar(getErrorMessage(err), "error");
    }
  };

  const fetchLeagueGames = async () => {
    if (!league || !user) return;

    try {
      const data = await gameService.listCompletedLeagueGamesByOthers(
        supabase,
        league.id,
      );
      setLeagueGames(data);
    } catch (err) {
      showSnackbar(getErrorMessage(err), "error");
    }
  };

  const fetchTeams = async () => {
    if (!league) return;

    try {
      const teamsData = await teamService.getAllLeagueTeams(
        supabase,
        league.id,
      );
      setTeams(teamsData);

      // Create teams lookup map
      const map: Record<string, Tables<"teams">> = {};
      teamsData.forEach((team: Tables<"teams">) => {
        map[team.id] = team;
      });
      setTeamsMap(map);
    } catch (err) {
      showSnackbar(getErrorMessage(err), "error");
    }
  };

  // Fetch data on mount
  useEffect(() => {
    const fetchLeagueMembers = async () => {
      if (!league) return;

      try {
        const members = await leagueService.getLeagueMembers(
          supabase,
          league.id,
        );
        setLeagueMembers(members);
      } catch (err) {
        showSnackbar(getErrorMessage(err), "error");
      }
    };

    const initializePage = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchMyGames(),
          fetchLeagueGames(),
          fetchTeams(),
          fetchLeagueMembers(),
        ]);
      } catch (err) {
        showSnackbar(getErrorMessage(err), "error");
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [league, user, setLoading, supabase]);

  const refreshMemberData = useCallback(async () => {
    if (!league) return;
    try {
      const [teamsData, membersData] = await Promise.all([
        teamService.getAllLeagueTeams(supabase, league.id),
        leagueService.getLeagueMembers(supabase, league.id),
      ]);
      setTeams(teamsData);
      setLeagueMembers(membersData);

      const map: Record<string, Tables<"teams">> = {};
      teamsData.forEach((team: Tables<"teams">) => {
        map[team.id] = team;
      });
      setTeamsMap(map);
    } catch (err) {
      showSnackbar(getErrorMessage(err), "error");
    }
  }, [league, supabase]);

  const onCreateNewGame = async () => {
    if (league) {
      setLoading(true);
      try {
        const game = await gameService.create(supabase, {
          name: newGameName,
          league_id: league.id,
          bonus_round_type: "Classic (List)",
        });
        await gameService.initializeFullGame(supabase, game.id);

        // Refetch to get joined profile data
        const updatedGames = await gameService.listMyLeagueGames(
          supabase,
          league.id,
          user!.id,
        );
        setMyGames(updatedGames);

        nav(`/game/${game.id}/builder`);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onCreateNewTeam = async () => {
    if (league?.id) {
      try {
        setLoading(true);
        const newTeamRes = await teamService.create(supabase, {
          name: newTeam.name,
          color: generateRandomHexCode(),
          league_id: league.id,
        });
        setTeams([...teams, { ...newTeamRes, members: [] }]);
        setNewTeam({
          name: "",
          color: "",
          league_id: league?.id ?? "",
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onConfirmStart = async () => {
    try {
      if (gameToStart) {
        setLoading(true);
        await gameService.update(supabase, gameToStart?.id, {
          is_started: true,
        });
        setLoading(false);
        nav(`/game/${gameToStart.id}/admin`);
      }
    } catch (err) {
      // Error handling
    } finally {
      setLoading(false);
    }
  };

  const onDeleteGame = async (game: App.GameWithCreator) => {
    try {
      setLoading(true);
      await gameService.update(supabase, game.id, {
        deleted_at: new Date().toISOString(),
      });
      await fetchLeagueGames();
      await fetchMyGames();
    } catch (err) {
      showSnackbar(getErrorMessage(err), "error");
    } finally {
      setLoading(false);
    }
  };

  const onCopyGameLink = async (game: App.GameWithCreator) => {
    await navigator.clipboard.writeText(
      `${window.location.origin}/game/${game.id}`,
    );
    showSnackbar("Game link copied to clipboard", "success");
  };

  const onCopyJoinCode = async () => {
    try {
      await navigator.clipboard.writeText(league.join_code);
      showSnackbar("League code copied to clipboard!", "success");
    } catch (err) {
      console.error(err);
    }
  };

  const onDeleteTeam = (idx: number) => {
    const updatedTeams = cloneDeep(teams);
    updatedTeams.splice(idx, 1);
    setTeams(updatedTeams);
  };

  const onSaveTeam = (idx: number, updatedTeam: App.TeamWithMembers) => {
    const updatedTeams = cloneDeep(teams);
    updatedTeams[idx] = updatedTeam;
    setTeams(updatedTeams);
  };

  return {
    user,
    signOut,
    nav,
    isMobile,

    myGames,
    leagueGames,
    teams,
    leagueMembers,
    teamsMap,
    loading,
    error,

    isOwner,
    currentUserMembership,
    unassignedMembers,

    activeTab,
    setActiveTab,
    gameToStart,
    setGameToStart,
    confirmSignOut,
    setConfirmSignOut,

    isEditingName,
    setIsEditingName,
    editedName,
    setEditedName,
    onSaveLeagueName,

    newGameName,
    setNewGameName,
    onCreateNewGame,

    newTeam,
    setNewTeam,
    onCreateNewTeam,

    onConfirmStart,
    onDeleteGame,
    onCopyGameLink,
    onCopyJoinCode,

    onDeleteTeam,
    onSaveTeam,
    refreshMemberData,
  };
};
